/**
 * User Groups Configuration
 *
 * This file centralizes all user group definitions, categories, and helper functions
 * for access control throughout the application.
 *
 * When adding a new group:
 * 1. Add it to USER_GROUPS constant
 * 2. Add it to the appropriate category array (PLUS_GROUPS, POS_GRADUACAO_GROUPS, etc.)
 * 3. If it's a Pós Graduação group, add the course URL mapping to POS_GRADUACAO_COURSE_URLS
 */

// =============================================================================
// GROUP NAME CONSTANTS
// =============================================================================

export const USER_GROUPS = {
  // Student/Subscription Groups
  ALUNOS: 'Alunos',
  NMA_ALUNOS: 'NMAAlunos',
  NMA_30_DIAS: 'NMA30dias',
  CIA_30_DIAS: 'CIA30Dias',
  CIA_PLUS_ANUAL: 'CIA_plus_anual',
  CIA_PLUS_EMPRESA: 'CIA_plus_empresa',
  CIA_PLUS_LONGO: 'CIA_plus_longo',

  // Role Groups
  MENTORES: 'Mentores',
  TUTOR: 'Tutor',
  CIA_MAAIS_PRO_GESTOR: 'cia_maais_pro_gestor',
  CIA_MAAIS_PRO_COLAB: 'cia_maais_pro_colab',

  // ABA Course Groups
  CURSO_ABA: 'CursoABA',
  CURSO_ABA_2021: 'CursoABA2021',
  CURSO_ABA_2022: 'CursoABA2022',

  // Pós Graduação Groups
  POS_GRADUACAO_T1: 'PosGraduacaoT1',
  POS_GRADUACAO_T2: 'PosGraduacaoT2',
  POS_GRADUACAO_T3: 'PosGraduacaoT3',
  POS_GRADUACAO_T4: 'PosGraduacaoT4',
  POS_GRADUACAO_T1_24: 'PosGraduacaoT1-24',
  POS_GRADUACAO_P: 'PosGraduacaoP',
  POS_TEA: 'Pos_TEA',
  POS_ABA_T1_25: 'Pos_ABA_T1_25',
  POS_ABA_T1_26: 'Pos_ABA_T1_26',
  MAAIS_RT: 'MAAIS_RT',

  // Feature Groups
  BANCO_DE_TALENTOS: 'BancoDeTalentos',
  SOCIAUTISM: 'Sociautism',
} as const;

export type UserGroup = (typeof USER_GROUPS)[keyof typeof USER_GROUPS];

// =============================================================================
// GROUP CATEGORIES
// =============================================================================

/**
 * Groups that grant Plus/Premium access to the platform.
 * Users with any of these groups can access Plus-restricted content.
 */
export const PLUS_GROUPS: readonly string[] = [
  USER_GROUPS.ALUNOS,
  USER_GROUPS.NMA_ALUNOS,
  USER_GROUPS.NMA_30_DIAS,
  USER_GROUPS.CIA_30_DIAS,
  USER_GROUPS.CIA_PLUS_ANUAL,
  USER_GROUPS.CIA_PLUS_EMPRESA,
  USER_GROUPS.CIA_PLUS_LONGO,
  USER_GROUPS.MENTORES,
  USER_GROUPS.TUTOR,
  USER_GROUPS.CIA_MAAIS_PRO_GESTOR,
  USER_GROUPS.CIA_MAAIS_PRO_COLAB,
];

/**
 * Groups that grant access to ABA Course content.
 */
export const ABA_COURSE_GROUPS: readonly string[] = [
  USER_GROUPS.CURSO_ABA,
  USER_GROUPS.CURSO_ABA_2021,
  USER_GROUPS.CURSO_ABA_2022,
  USER_GROUPS.MENTORES,
  USER_GROUPS.TUTOR,
];

/**
 * All Pós Graduação groups.
 * Used to determine if user should see Pós Graduação menu item.
 */
export const POS_GRADUACAO_GROUPS: readonly string[] = [
  USER_GROUPS.POS_GRADUACAO_T1,
  USER_GROUPS.POS_GRADUACAO_T2,
  USER_GROUPS.POS_GRADUACAO_T3,
  USER_GROUPS.POS_GRADUACAO_T4,
  USER_GROUPS.POS_GRADUACAO_T1_24,
  USER_GROUPS.POS_GRADUACAO_P,
  USER_GROUPS.POS_TEA,
  USER_GROUPS.POS_ABA_T1_25,
  USER_GROUPS.POS_ABA_T1_26,
  USER_GROUPS.MAAIS_RT,
];

/**
 * Groups that can view/manage curriculum (Banco de Talentos).
 * Note: PosGraduacaoP is excluded from this list.
 */
export const CURRICULO_GROUPS: readonly string[] = [
  USER_GROUPS.POS_GRADUACAO_T1,
  USER_GROUPS.POS_GRADUACAO_T2,
  USER_GROUPS.POS_GRADUACAO_T3,
  USER_GROUPS.POS_GRADUACAO_T4,
  USER_GROUPS.POS_GRADUACAO_T1_24,
  USER_GROUPS.POS_TEA,
  USER_GROUPS.POS_ABA_T1_25,
  USER_GROUPS.POS_ABA_T1_26,
  USER_GROUPS.MAAIS_RT,
];

// =============================================================================
// PÓS GRADUAÇÃO COURSE URL MAPPING
// =============================================================================

/**
 * Maps each Pós Graduação group to its specific course URL.
 * Used when user has only one Pós group to redirect directly to the course.
 */
export const POS_GRADUACAO_COURSE_URLS: Record<string, string> = {
  [USER_GROUPS.POS_GRADUACAO_T1]: '/cursos/pos-graduacao-em-autismo-21',
  [USER_GROUPS.POS_GRADUACAO_T2]: '/cursos/pos-graduacao-em-autismo-t2',
  [USER_GROUPS.POS_GRADUACAO_T3]: '/cursos/pos-graduacao-em-autismo-t3',
  [USER_GROUPS.POS_GRADUACAO_T4]: '/cursos/pos-graduacao-em-autismo-t4',
  [USER_GROUPS.POS_GRADUACAO_T1_24]: '/cursos/pos-graduacao-em-autismo-24-t1',
  [USER_GROUPS.POS_GRADUACAO_P]: '/cursos/pos-graduacao-em-autismo-24-t2',
  [USER_GROUPS.POS_TEA]: '/cursos/pos-graduacao-em-autismo',
  [USER_GROUPS.POS_ABA_T1_25]: '/cursos/pos-graduacao-aba-t1-25',
  [USER_GROUPS.POS_ABA_T1_26]: '/cursos/pos-graduacao-aba-t1-26',
  [USER_GROUPS.MAAIS_RT]: '/cursos/pos-graduacao-aba-t1-25',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Parses the comma-separated grupos string into an array of group names.
 */
export const parseUserGroups = (grupos: string): string[] => {
  if (!grupos) return [];
  return grupos.split(',').map((g) => g.trim()).filter(Boolean);
};

/**
 * Checks if the user has at least one group from the target groups array.
 */
export const hasAnyGroup = (userGroups: string[], targetGroups: readonly string[]): boolean => {
  return userGroups.some((g) => targetGroups.includes(g));
};

/**
 * Checks if the user has a specific group.
 */
export const hasGroup = (userGroups: string[], group: string): boolean => {
  return userGroups.includes(group);
};

/**
 * Checks if the user has Plus/Premium access.
 */
export const checkIsPlus = (grupos: string): boolean => {
  const userGroups = parseUserGroups(grupos);
  return hasAnyGroup(userGroups, PLUS_GROUPS);
};

/**
 * Checks if the user has access to ABA Course content.
 */
export const checkAlunoABA = (grupos: string): boolean => {
  const userGroups = parseUserGroups(grupos);
  return hasAnyGroup(userGroups, ABA_COURSE_GROUPS);
};

/**
 * Checks if the user should see the Pós Graduação menu.
 * Includes Tutor role which can access all Pós Graduação content.
 */
export const checkShowPos = (grupos: string): boolean => {
  const userGroups = parseUserGroups(grupos);
  return hasAnyGroup(userGroups, POS_GRADUACAO_GROUPS) || hasGroup(userGroups, USER_GROUPS.TUTOR);
};

/**
 * Checks if the user can view/manage curriculum.
 * Includes Tutor role.
 */
export const checkShowCurriculo = (grupos: string): boolean => {
  const userGroups = parseUserGroups(grupos);
  return hasAnyGroup(userGroups, CURRICULO_GROUPS) || hasGroup(userGroups, USER_GROUPS.TUTOR);
};

/**
 * Get the appropriate link for Pós Graduação based on user groups.
 *
 * Logic:
 * - If user is Tutor or has 2+ Pós groups: returns hub page (/pos-graduacao)
 * - If user has exactly 1 Pós group: returns direct course URL
 * - Otherwise: returns empty string
 */
export const getPosGraduacaoLink = (grupos: string): string => {
  const userGroups = parseUserGroups(grupos);
  const userPosGroups = userGroups.filter((g) => POS_GRADUACAO_GROUPS.includes(g));

  const isTutor = hasGroup(userGroups, USER_GROUPS.TUTOR);

  // If tutor or has 2+ Pós groups, go to hub
  if (isTutor || userPosGroups.length >= 2) {
    return '/pos-graduacao';
  }

  // If has exactly 1 Pós group, go directly to that course
  if (userPosGroups.length === 1) {
    const group = userPosGroups[0];
    return POS_GRADUACAO_COURSE_URLS[group] || '';
  }

  return '';
};

// =============================================================================
// GROUP DISPLAY NAMES (mapeamento grupo → nome legível do curso)
// =============================================================================

export const GROUP_DISPLAY_NAMES: Record<string, string> = {
  AbaNaEdInfantil: 'ABA na Educação Infantil',
  ABAnaPratica: 'ABA na Prática',
  AdaptMatEsc: 'Adaptando Materiais Escolares',
  AdolescentesEAdultos: 'Habilidades Sociais para Adolescentes e Adultos com TEA',
  Alfabetizacao: 'Curso de Alfabetização no Autismo',
  AvaliacaoNeuropsicologica: 'Avaliação Neuropsicológica no TEA',
  CapacitacaoInclusao: 'Capacitação Avançada em Inclusão Escolar de Alunos com TEA',
  ComAlt2021: 'Curso Avançado de Comunicação Alternativa',
  ComunicacaoAlternativa: 'Curso Avançado de Comunicação Alternativa',
  ComunicacaoAlternativa_2020_2: 'Curso Avançado de Comunicação Alternativa',
  CursoABA: 'Curso em Terapia ABA para Pais e Aplicadores',
  CursoABA2021: 'Curso em Terapia ABA para Pais e Aplicadores',
  CursoABA2022: 'Curso em Terapia ABA para Pais e Aplicadores',
  DesenvolvimentoMotor: 'Práticas para o Desenvolvimento Motor de Pessoas com Autismo',
  EdFisica: 'Educação Física Adaptada para Pessoas com Autismo',
  EstrategiasMusicais: 'Estratégias Musicais para Inclusão Escolar',
  Formacao: 'Formação Avançada em Terapia Comportamental',
  Formacao2018: 'Formação Avançada em Terapia Comportamental',
  Formacao2020: 'Formação Avançada em Terapia ABA',
  Formacao2021: 'Formação Avançada em ABA',
  FuncoesExecutivas: 'Curso de Estimulação das Funções Executivas na Prática',
  HabilidadesBasicas: 'Treino de Habilidades Básicas para Pessoas com Autismo',
  Imersao2019: 'Imersão em Terapia Comportamental no Autismo 2019',
  Inclusao2019: 'Semana da Inclusão Escolar no TEA 2019',
  Masterclass: 'Masterclass em Inclusão Escolar no TEA',
  Masterclass2020: 'Masterclass em Inclusão Escolar no TEA',
  Matematica: 'Curso de Ensino de Matemática para Alunos com Autismo',
  MiniCursoAplicadoresABA: 'Minicurso para Aplicadores',
  NMA30Dias: 'Assinatura',
  NMA7Dias: 'Assinatura',
  NMAAlunos: 'Assinatura',
  'portage-t1': 'Capacitação em Inventário Portage Operacionalizado',
  PosGraduacaoT1: 'Pós Graduação em Autismo',
  PosGraduacaoT2: 'Pós Graduação em Autismo',
  RecursosMusicais: 'Recursos Musicais para Prática Terapêutica de pessoas com Autismo',
  TEAnasIgrejas: 'Curso de Inclusão Missionária de Pessoas com TEA em Igrejas e Comunidades de Fé',
  vbmapp: 'Curso Avançado em Protocolo VB-MAPP',
  vbmapp2020: 'Curso Avançado em Protocolo VB-MAPP',
  vbmapp2021: 'Curso Avançado em Protocolo VB-MAPP',
  'VBMAPP-T1-2022': 'Curso Avançado em Protocolo VB-MAPP',
  'vbmapp-t2-2021': 'Curso Avançado em Protocolo VB-MAPP',
};

/**
 * Converte array de grupos em nomes legíveis de cursos.
 */
export const groupToCourseName = (groups: string[]): string => {
  return groups.map((g) => GROUP_DISPLAY_NAMES[g] || g).join(', ');
};
